import { uniq } from 'lodash-es'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { func, pattern, required, validationContext } from 'validx'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { SearchableSelectViewModel } from '@/infra/SearchableSelectViewModel'
import type {
  BulkCreateCustomersFn,
  CreateCustomerFn,
  CreateMemberManuallyFn,
  GetCurrentMemberFn,
  SearchMembersFn,
} from '@/features/iam'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { whileActive } from '@/utils/rx-util'
import { Id } from '@/infra/id'
import { toStream } from '@/utils/mobx-util'
import { pluralize } from '@/utils/pluralization-util'
import { match } from '@/utils/pattern-match-util'
import type { BulkSubmissionResult } from '@/features/iam/customers/bulk-create-util'
import {
  type ParsedCustomerRow,
  parseCsvFile,
} from '@/features/iam/customers/customer-csv'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SearchableSelectQuery } from '@/infra/SearchableSelectViewModel'

interface InviteResults {
  email: string
  success?: boolean
  error?: Error
}

/**
 * Sign-in type selection.
 */
export type SignInType = 'UsernamePassword' | 'AccessLink'

export class ClientInvitationDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel()

  /**
   * The current content of the email address text area (for Username & Password mode).
   */
  @observable
  accessor emailAddressTextArea = ''

  /**
   * The currently selected sign-in type.
   */
  @observable
  accessor signInType: SignInType = 'UsernamePassword'

  /**
   * Customer given name (first name).
   */
  @observable
  accessor customerGivenName = ''

  /**
   * Customer family name (last name).
   */
  @observable
  accessor customerFamilyName = ''

  /**
   * Customer phone number.
   */
  @observable
  accessor customerPhoneNumber = ''

  /**
   * Customer email address.
   */
  @observable
  accessor customerEmail = ''

  /**
   * Whether bulk upload mode is active for customer invitations.
   */
  @observable
  accessor bulkUploadMode = false

  /**
   * Parsed customer data from CSV upload.
   */
  @observable
  accessor bulkCustomers: ParsedCustomerRow[] = []

  /**
   * Error message from CSV parsing (header errors, etc.).
   */
  @observable
  accessor bulkUploadError = ''

  /**
   * Name of the uploaded CSV file.
   */
  @observable
  accessor bulkUploadFileName = ''

  /**
   * Results from the last bulk submission (for showing results view).
   */
  @observable.ref
  accessor bulkSubmissionResults: BulkSubmissionResult[] = []

  /**
   * Whether to show the results view after bulk submission.
   */
  @observable
  accessor showBulkResults = false

  /**
   * Advisor picker. Pages members who can be assigned.
   */
  readonly advisorSelect = new SearchableSelectViewModel((query, signal) =>
    this.searchAdvisors(query, signal),
  )

  /**
   * Validation rules for regular workspace invitations.
   */
  invitationValidation = validationContext(this, {
    emailAddressTextArea: [
      func(
        () => this.emailAddresses.length > 0,
        'You must enter at least one valid email address.',
      ),
    ],
  })

  /**
   * Validation rules for customer invitations.
   */
  customerValidation = validationContext(this, {
    customerEmail: [
      required(),
      pattern('email'),
      func(({ value }: { value: string }) => {
        return value.length <= 320 || "Email can't be more than 320 characters"
      }),
      func(
        () => this.customerEmail.trim().length > 0,
        'You must enter a valid email address',
      ),
    ],
    customerGivenName: [
      func(
        () => this.customerGivenName.trim().length > 0,
        'You must enter a first name',
      ),
    ],
    customerFamilyName: [
      func(
        () => this.customerFamilyName.trim().length > 0,
        'You must enter a last name',
      ),
    ],
    customerPhoneNumber: [
      func(
        (ctx) =>
          // Only run the phone number rule if we have a value,
          // as phone numbers are optional.
          !ctx.value ||
          pattern(
            // Super basic regex for phone number chars.
            /^[+]?[ 0-9()-]+$/i,
            'This does not look like a phone number.',
          )(ctx),
      ),
      func(({ value }: { value: string }) => {
        return value.length <= 20 || "Phone can't be more than 20 characters"
      }),
    ],
  })

  constructor(
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _searchMembersByPermission: SearchMembersFn,
    private readonly _createMemberManually: CreateMemberManuallyFn,
    private readonly _createCustomer: CreateCustomerFn,
    private readonly _bulkCreateCustomers: BulkCreateCustomersFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this._lifecycle.manage(
      this.advisorSelect.query$.pipe(
        whileActive(
          toStream(() => this.modal.showing && this.canChooseAdvisor, true),
        ),
      ),
    )
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The current workspace.
   */
  @computed
  get workspace() {
    return this._getCurrentMember().workspace
  }

  /**
   * A list of valid email addresses extracted from email address text area.
   */
  @computed
  get emailAddresses() {
    return uniq(
      this.emailAddressTextArea
        .split(/[,|\n]/)
        .map((e) => e.trim().toLowerCase())
        .filter(
          (e) =>
            !!e &&
            validationContext(
              { email: e },
              { email: [pattern('email')] },
            ).validate().isValid,
        ),
    )
  }

  /**
   * Whether to use access link sign-in type.
   */
  @computed
  get canUseAccessLink() {
    const canCreateCustomers =
      this._getCurrentMember().permissions.customerCreate
    const externalCustomerConfig = this.workspace.config.externalCustomer
    const workspaceSupportsCustomers =
      externalCustomerConfig.enabled &&
      externalCustomerConfig.allowRequestingMagicLinks

    return canCreateCustomers && workspaceSupportsCustomers
  }

  /**
   * Whether to use username/password sign-in type.
   */
  @computed
  get canUseUsernameAndPassword() {
    return this._getCurrentMember().permissions.adminUserCreate
  }

  /**
   * Whether invited clients can be assigned an advisor in this workspace.
   */
  @computed
  get clientAssignmentEnabled() {
    return this.workspace.config.externalCustomer.clientAssignmentEnabled
  }

  /**
   * The invitation dialog's description for the selected sign-in type.
   */
  @computed
  get dialogDescription() {
    return this.signInType === 'AccessLink'
      ? `Fill in the customer information to allow access link-based sign in.${this.assignsToSelf ? " They'll be assigned to you as their advisor." : ''}`
      : "Fill in the emails of the users you'd like to invite to the Customer Portal."
  }

  /**
   * Whether every client this member invites is assigned to them.
   */
  @computed
  get assignsToSelf() {
    const member = this._getCurrentMember()
    return (
      this.clientAssignmentEnabled &&
      member.permissions.clientAssignmentManage &&
      !member.permissions.workspaceClientAssignmentManage
    )
  }

  /**
   * Whether the current member can choose which advisor to assign.
   */
  @computed
  get canChooseAdvisor() {
    const member = this._getCurrentMember()
    return (
      this.clientAssignmentEnabled &&
      member.permissions.workspaceClientAssignmentManage
    )
  }

  /**
   * The advisor to assign invited clients to, if any.
   */
  @computed
  get assignedAdvisorUserPublicId(): Id {
    if (this.assignsToSelf) {
      return this._getCurrentMember().user.publicId
    }
    return this.advisorSelect.selected?.user.publicId || Id.none
  }

  /**
   * Get the list of valid bulk customers (no validation errors).
   */
  @computed
  get validBulkCustomers() {
    return this.bulkCustomers.filter((c) => c.isValid)
  }

  /**
   * Get successful results from bulk submission.
   */
  @computed
  get successfulBulkResults() {
    return this.bulkSubmissionResults.filter((r) => r.success)
  }

  /**
   * Get failed results from bulk submission.
   */
  @computed
  get failedBulkResults() {
    return this.bulkSubmissionResults.filter((r) => !r.success)
  }

  /**
   * Set the email addresses text area.
   */
  @action.bound
  setEmailAddressTextArea(emails: string) {
    this.emailAddressTextArea = emails
  }

  /**
   * Set the sign-in type.
   */
  @action.bound
  setSignInType(type: SignInType) {
    this.signInType = type
  }

  /**
   * Pages members who can be chosen as an advisor. Searches members so that
   * platform-role permissions are included.
   */
  searchAdvisors = (query: SearchableSelectQuery, signal?: AbortSignal) =>
    this._searchMembersByPermission(
      {
        workspaceId: this.workspace.id,
        userTypes: ['User'],
        permission: 'clientAssignmentManage',
        searchText: query.searchText,
        limit: query.limit,
        after: query.after,
      },
      signal,
    )

  /**
   * Set the customer given name.
   */
  @action.bound
  setCustomerGivenName(name: string) {
    this.customerGivenName = name
  }

  /**
   * Set the customer family name.
   */
  @action.bound
  setCustomerFamilyName(name: string) {
    this.customerFamilyName = name
  }

  /**
   * Set the customer phone number.
   */
  @action.bound
  setCustomerPhoneNumber(phone: string) {
    this.customerPhoneNumber = phone
  }

  /**
   * Set the customer email.
   */
  @action.bound
  setCustomerEmail(email: string) {
    this.customerEmail = email
  }

  /**
   * Set bulk upload mode.
   */
  @action.bound
  setBulkUploadMode(mode: boolean) {
    this.bulkUploadMode = mode
    if (!mode) {
      this.clearBulkUpload()
    }
  }

  /**
   * Clear bulk upload state.
   */
  @action.bound
  clearBulkUpload() {
    this.bulkCustomers = []
    this.bulkUploadError = ''
    this.bulkUploadFileName = ''
    this.bulkSubmissionResults = []
    this.showBulkResults = false
  }

  /**
   * Dismiss the bulk results view.
   */
  @action.bound
  dismissBulkResults() {
    this.close()
  }

  /**
   * Dismiss the bulk results view, keeping the failed entries in the
   * customer list for a retry.
   */
  @action.bound
  retryFailedBulkResults() {
    if (this.failedBulkResults.length === 0) {
      this.close()
      return
    }

    this.bulkCustomers = this.bulkCustomers.filter((c) =>
      this.failedBulkResults.some((f) => f.email === c.email),
    )
    this.showBulkResults = false
    this.bulkSubmissionResults = []
  }

  /**
   * Handle CSV file upload and parse its contents.
   */
  @action.bound
  async handleCsvUpload(file: File) {
    this.bulkUploadError = ''
    this.bulkUploadFileName = file.name

    try {
      const result = await parseCsvFile(file)

      runInAction(() => {
        if (result.headerError) {
          this.bulkUploadError = result.headerError
          this.bulkCustomers = []
          return
        }

        this.bulkCustomers = result.rows
      })
    } catch {
      runInAction(() => {
        this.bulkUploadError = 'Failed to read CSV file'
        this.bulkCustomers = []
      })
    }
  }

  /**
   * Remove a customer from the bulk upload list.
   */
  @action.bound
  removeBulkCustomer(index: number) {
    this.bulkCustomers.splice(index, 1)
  }

  /**
   * Initializes the dialog based on available permissions.
   */
  @action.bound
  initialize() {
    if (this.canUseAccessLink && !this.canUseUsernameAndPassword) {
      this.setSignInType('AccessLink')
      return
    }

    this.setSignInType('UsernamePassword')
  }

  /**
   * Opens the dialog.
   */
  @action.bound
  show() {
    this.initialize()
    this.modal.show()
  }

  /**
   * Clears client invitation dialog state.
   */
  @action.bound
  reset() {
    this.setEmailAddressTextArea('')
    this.setSignInType('UsernamePassword')
    this.setCustomerGivenName('')
    this.setCustomerFamilyName('')
    this.setCustomerPhoneNumber('')
    this.setCustomerEmail('')
    this.setBulkUploadMode(false)
    this.advisorSelect.reset()

    this.customerValidation.reset()
    this.invitationValidation.reset()
  }

  /**
   * Send regular workspace invitations to the specified users.
   */
  private async _submitInvitations(): Promise<void> {
    if (!this.invitationValidation.reset().validate().isValid) {
      return
    }

    const results: InviteResults[] = await Promise.all(
      this.emailAddresses.map((e) =>
        this._createMemberManually({
          email: e,
          workspaceId: Id.assert(this.workspace.id),
          // The backend assigns the default role.
          roleId: undefined,
          skillIds: [],
          teamId: undefined,
          teamRoleId: undefined,
          source: 'Local',
          portal: 'CustomerPortal',
        })
          .then(() => {
            return { email: e, success: true }
          })
          .catch((err: Error) => {
            return { email: e, error: err }
          }),
      ),
    )

    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => r.error)

    // If all invitations succeeded, close dialog.
    if (failed.length < 1) {
      this._newAlert()
        .withState('success')
        .saying(
          'Success!',
          `Invitations sent to ${successful.length} ${pluralize('user', successful.length)}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
      return
    }

    // If any invitations failed, populate email text area with the failed email addresses.
    this._newAlert()
      .withState('error')
      .saying(
        `Failed to send invitations to ${failed.length} ${pluralize('user', failed.length)}.`,
      )
      .autoDismiss(2500)
      .show()
    this.setEmailAddressTextArea(failed.map((f) => f.email).join(', '))
  }

  /**
   * Send customer invitation (single mode).
   */
  private async _submitSingleCustomer(): Promise<void> {
    if (!this.customerValidation.reset().validate().isValid) {
      return
    }

    await this._createCustomer({
      workspaceId: Id.assert(this.workspace.id),
      email: this.customerEmail,
      givenName: this.customerGivenName,
      familyName: this.customerFamilyName,
      phoneNumber: this.customerPhoneNumber || undefined,
      advisorUserPublicId: this.assignedAdvisorUserPublicId,
    }).catch(this._createAlertErrorHandler('Sending the invitation failed'))

    this._newAlert()
      .withState('success')
      .saying('Success!', `Customer invitation sent to ${this.customerEmail}.`)
      .autoDismiss(2500)
      .show()
    this.close()
  }

  /**
   * Send customer invitations (bulk mode).
   * Processes customers in batches of 25 with a delay between batches to avoid rate limiting.
   */
  private async _submitBulkCustomers(): Promise<void> {
    const customers = this.validBulkCustomers
    if (customers.length === 0) {
      this._newAlert()
        .withState('error')
        .saying('No valid customers to create.')
        .autoDismiss(2500)
        .show()
      return
    }

    const results = await this._bulkCreateCustomers({
      workspaceId: this.workspace.id,
      customers,
      advisorUserPublicId: this.assignedAdvisorUserPublicId,
    })

    runInAction(() => {
      this.bulkSubmissionResults = results
      this.showBulkResults = true
    })
  }

  /**
   * Send customer invitation (dispatches to single or bulk mode).
   */
  private async _submitCustomer(): Promise<void> {
    if (this.bulkUploadMode) {
      return this._submitBulkCustomers()
    }
    return this._submitSingleCustomer()
  }

  /**
   * Submit the form based on the selected invitation type.
   */
  submit = task.resolved(async () => {
    return match(this.signInType, {
      UsernamePassword: () => this._submitInvitations(),
      AccessLink: () => this._submitCustomer(),
    })
  })

  /**
   * Close the dialog and reset selections to default.
   */
  @action.bound
  close() {
    this.reset()
    this.modal.close()
  }
}
